import { useEffect, useMemo, useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { EntityCard, EntityList, SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { kyc } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  FormActions,
  Input,
  Loading,
  Select,
} from '@/components/ui';
import { str } from '@/utils/format';

const DOC_TYPE_LABELS: Record<string, string> = {
  identity: 'توثيق الهوية',
  regulatory: 'أوراق الهيئة الناظمة',
  national_id: 'هوية شخصية',
  passport: 'جواز سفر',
};

function UploadField({
  label,
  hint,
  file,
  onChange,
  required,
  accept = 'image/png,image/jpeg,image/webp',
}: {
  label: string;
  hint?: string;
  file: File | null;
  onChange: (file: File | null) => void;
  required?: boolean;
  accept?: string;
}) {
  const isImage = !!file && file.type.startsWith('image/');
  const preview = useMemo(() => (isImage && file ? URL.createObjectURL(file) : ''), [file, isImage]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  return (
    <label className="field ui-field kyc-upload-field">
      <span>
        {label}
        {required ? ' *' : ''}
      </span>
      <div className={`kyc-upload-box${file ? ' has-file' : ''}`}>
        <div className="image-thumb kyc-upload-thumb">
          {preview ? <img src={preview} alt="" /> : <Icon name="package" size={22} />}
        </div>
        <div className="kyc-upload-meta">
          <strong>{file ? file.name : 'اختر ملفاً من الجهاز'}</strong>
          {hint ? <small className="muted">{hint}</small> : null}
          <span className="image-upload-btn kyc-upload-btn">
            <Icon name="package" size={16} />
            {file ? 'تغيير الملف' : 'رفع ملف'}
            <input
              type="file"
              accept={accept}
              required={required && !file}
              onChange={(e) => onChange(e.target.files?.[0] ?? null)}
            />
          </span>
        </div>
      </div>
    </label>
  );
}

export function KycScreen() {
  const state = useAsync(
    () => kyc.me(),
    (p) => ({
      documents: pickList(p, 'documents', 'items', 'data'),
      user: p.user && typeof p.user === 'object' ? (p.user as JsonMap) : null,
    }),
  );

  const isDistributor = str(state.data?.user?.role) === 'distributor';
  const [docKind, setDocKind] = useState<'national_id' | 'passport' | 'regulatory'>('national_id');
  const [fullName, setFullName] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [address, setAddress] = useState('');
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [regulatoryFile, setRegulatoryFile] = useState<File | null>(null);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state.data?.user) return;
    setFullName((v) => v || str(state.data?.user?.name));
    setAddress((v) => v || str(state.data?.user?.address));
  }, [state.data?.user]);

  const isRegulatory = docKind === 'regulatory';
  const needsBack = docKind === 'national_id';
  const documentLabel =
    docKind === 'passport' ? 'صورة جواز السفر' : docKind === 'national_id' ? 'صورة الهوية (الوجه الأمامي)' : 'صورة / ملف الوثيقة';

  async function submit() {
    if (isRegulatory) {
      if (!regulatoryFile) {
        setMsgOk(false);
        setMsg('يرجى رفع ملف أو صورة أوراق الهيئة.');
        return;
      }
    } else {
      if (!front || !selfie || (needsBack && !back)) {
        setMsgOk(false);
        setMsg(
          needsBack
            ? 'يرجى رفع صورة الوجه الأمامي والخلفي وصورة شخصية مع الوثيقة.'
            : 'يرجى رفع صورة الوثيقة وصورة شخصية مع الوثيقة.',
        );
        return;
      }
    }

    const body = new FormData();
    body.append('doc_type', isRegulatory ? 'regulatory' : 'identity');
    body.append('full_name', fullName.trim());
    body.append('national_id', nationalId.trim());
    body.append('address', address.trim());

    if (isRegulatory && regulatoryFile) {
      body.append('document', regulatoryFile);
    } else {
      if (front) body.append('identity_front', front);
      if (back) body.append('identity_back', back);
      else if (front) body.append('identity_back', front);
      if (selfie) body.append('selfie_with_id', selfie);
    }

    setBusy(true);
    setMsg('');
    const res = await kyc.submit(body);
    setBusy(false);
    if (!res.success) {
      setMsgOk(false);
      setMsg(errorMessage(res));
      return;
    }
    setMsgOk(true);
    setMsg(str(res.message, 'تم إرسال الوثائق للمراجعة.'));
    setFront(null);
    setBack(null);
    setSelfie(null);
    setRegulatoryFile(null);
    setNationalId('');
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const docs = state.data?.documents ?? [];
  const typeOptions = [
    { value: 'national_id', label: 'هوية شخصية' },
    { value: 'passport', label: 'جواز سفر' },
    ...(isDistributor ? [{ value: 'regulatory', label: 'أوراق الهيئة الناظمة' }] : []),
  ];

  return (
    <ScreenFrame mode="customer" title="الوثائق" subtitle="ارفع صور الوثائق للمراجعة والتوثيق.">
      <SectionCard title="رفع وثيقة" subtitle="اختر نوع الوثيقة ثم ارفع الصور المطلوبة بوضوح.">
        <Form className="kyc-form" onSubmit={() => void submit()}>
          <Select
            label="نوع الوثيقة"
            value={docKind}
            onChange={(v) => {
              setDocKind(v as typeof docKind);
              setFront(null);
              setBack(null);
              setSelfie(null);
              setRegulatoryFile(null);
            }}
            options={typeOptions}
          />
          <Input label="الاسم الكامل" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <Input
            label={isRegulatory ? 'رقم السجل / المعرف' : 'رقم الوثيقة'}
            value={nationalId}
            onChange={(e) => setNationalId(e.target.value)}
          />
          <Input label="العنوان" value={address} onChange={(e) => setAddress(e.target.value)} />

          <div className="kyc-uploads">
            {isRegulatory ? (
              <UploadField
                label="ملف / صورة الوثيقة"
                hint="صورة واضحة أو PDF حسب المتاح"
                file={regulatoryFile}
                onChange={setRegulatoryFile}
                accept="image/png,image/jpeg,image/webp,application/pdf"
                required
              />
            ) : (
              <>
                <UploadField
                  label={documentLabel}
                  hint="ارفع صورة واضحة للوثيقة حسب النوع المختار"
                  file={front}
                  onChange={setFront}
                  required
                />
                {needsBack ? (
                  <UploadField
                    label="صورة الهوية (الوجه الخلفي)"
                    hint="الوجه الخلفي للهوية الشخصية"
                    file={back}
                    onChange={setBack}
                    required
                  />
                ) : null}
                <UploadField
                  label="صورة شخصية مع الوثيقة"
                  hint="صورة واضحة لك وأنت تحمل الوثيقة"
                  file={selfie}
                  onChange={setSelfie}
                  required
                />
              </>
            )}
          </div>

          <div className="kyc-form-footer">
            <FlashMessage message={msg} tone={msgOk ? 'ok' : 'error'} />
            <FormActions className="kyc-submit-row">
              <Button type="submit" disabled={busy} className="kyc-submit-btn">
                {busy ? 'جاري الإرسال…' : 'إرسال للمراجعة'}
              </Button>
            </FormActions>
          </div>
        </Form>
      </SectionCard>

      <SectionCard title="الوثائق السابقة">
        {docs.length === 0 ? (
          <Empty title="لا توجد وثائق مرسلة" hint="بعد الإرسال ستظهر الطلبات هنا مع حالتها." />
        ) : (
          <EntityList>
            {docs.map((d, i) => (
              <EntityCard
                key={str(d.id, String(i))}
                title={DOC_TYPE_LABELS[str(d.doc_type ?? d.type)] ?? str(d.doc_type ?? d.type)}
                meta={str(d.national_id ?? d.document_number ?? d.number, str(d.full_name, '—'))}
                status={d.status}
              />
            ))}
          </EntityList>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
